import React, { useEffect, useMemo } from 'react';
import fr from 'facet-rest';
import {
  Redirect, Route, Switch, useLocation,
} from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  deprecatedClientAccountManagement,
  clientGoalsPath,
  clientMeeting, clientPage,
  clientPaths,
  deprecatedPWAPaths,
} from 'Consts/Strings';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import Meeting from 'Components/UI/Meeting';
import Router from 'Components/UI/FacetRouter/FacetRouter';
import Blueprint from 'Components/Blueprint';
import useResource from 'Hooks/useResource';
import relationships from 'Consts/Relationships';
import FullLayout from 'Components/Layout/Full';
import classNames from 'classnames';
import { useLayoutContext } from 'Context/LayoutContext';
import useUserContext from 'Context/UserContext';
import featureEnabled, { pickComponentAndProps } from 'Helpers/Affordances';
import MyMeetingsContainer from 'Components/ClientApp/CombinedApp/Viewport/MyMeetings/MyMeetingsContainer';
import { buildMemberMessagesPageLink, buildPageLink } from 'Views/Page/Content/Templates';
import Footer from 'Components/ClientApp/Desktop/Footer';
import useMobileLayout from 'Hooks/useMobileLayout';
import DefaultOrFDContentContainer from 'Components/Layout/DefaultOrFDContentContainer';
import { balanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';
import { ModalProvider } from 'Views/Page/Contexts/ModalContext';
import AnnouncementDialog from 'Context/AnnouncementContext/Dialog';
import { withUnknownPageCollectionType } from './withUnknownPageCollectionType';
import Dashboard from './Dashboard';
import SecureFiles from './SecureFiles';
import Questionnaire from './Questionnaire';
import { FileRow } from './SecureFiles/FileRow';
import Goals from './Goals';
import BlueprintProvider from '../../../../Context/BlueprintContext';
import Flow from './Flow';

const useStyles = makeStyles((theme) => ({
  desktopRoot: {
    // Used by the footer with new nav. The old nav put this in DesktopLayout.jsx
    position: 'relative',
    minHeight: `calc(100vh - ${theme.heights.clientNavigationHeight}px)`,
  },
  rootWithFooter: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
}));

const Viewport = () => {
  const isMobile = useMobileLayout();
  const { userResource } = useUserContext();
  const {
    householdResource,
    householdId,
  } = useSimpleHousehold();
  const { isFooterVisible, setNavigationVisible, setHideBottomNavigation } = useLayoutContext();
  const { resource: clientPlannerResource, initializeResource } = useResource(
    null,
  );

  const classes = useStyles();

  const location = useLocation();

  // If you navigate from a page to here, that page may have set some navigation settings that need to be reset
  useEffect(() => {
    setNavigationVisible(true);
    setHideBottomNavigation(false);
  }, [setNavigationVisible, setHideBottomNavigation]);

  React.useEffect(() => {
    const removeChat = document.getElementById('embedded-messaging');
    const removeFrame = document.getElementById('embeddedMessagingSiteContextFrame');
    if (removeChat) {
      removeChat.parentNode.removeChild(removeChat);
    }
    if (removeFrame) {
      removeFrame.parentNode.removeChild(removeFrame);
    }
  }, [householdResource]);

  useEffect(() => {
    if (householdResource?.relationship('planner')?.link('related')) {
      householdResource
        .relationship('planner')
        .link('related')
        .Get()
        .then(res => {
          initializeResource(res.resources().head());
        });
    }
  }, [householdResource]);

  const renderDashboard = useMemo(() => withUnknownPageCollectionType(() => <Dashboard />), []);
  const renderGoals = useMemo(() => withUnknownPageCollectionType(() => {
    return (
      featureEnabled(
        householdResource,
        relationships.goalsPageAndBanner,
      ) && (
        <FullLayout>
          <Goals />
        </FullLayout>
      )
    );
  }), []);
  const renderFiles = useMemo(() => withUnknownPageCollectionType(() => (
    <DefaultOrFDContentContainer>
      <SecureFiles fileRowComponent={FileRow} />
    </DefaultOrFDContentContainer>
  )), []);
  const renderMyMeetings = useMemo(() => withUnknownPageCollectionType(() => (
    <DefaultOrFDContentContainer>
      <MyMeetingsContainer />
    </DefaultOrFDContentContainer>
  )), []);

  const renderQuestionnaire = useMemo(() => withUnknownPageCollectionType(() => (
    <DefaultOrFDContentContainer test="questionnaire-page">
      <Questionnaire />
    </DefaultOrFDContentContainer>
  )), []);

  const defaultMeetingProps = {
    householdResource,
    leadPlannerResource: clientPlannerResource,
  };
  const {
    component: MeetingComponent,
    props: meetingComponentProps,
  } = useMemo(() => pickComponentAndProps(userResource, {
    [fr.relationships.activityFeed.name]: [
      Meeting,
      {
        ...defaultMeetingProps,
        sendMessageDisabled: true,
      },
    ],
    [fr.relationships.default.name]: [Meeting, defaultMeetingProps],
  }), [userResource]);

  const renderClientMeeting = useMemo(() => withUnknownPageCollectionType(() => (householdResource ? (
    <MeetingComponent {...meetingComponentProps} />
  ) : null)), [meetingComponentProps, MeetingComponent]);
  const renderBlueprint = useMemo(() => withUnknownPageCollectionType(() => (
    <BlueprintProvider>
      <FullLayout>
        <Blueprint />
      </FullLayout>
    </BlueprintProvider>
  )), []);
  const renderFlow = useMemo(() => withUnknownPageCollectionType(Flow), []);

  if (householdId === 0) {
    return <div>Loading...</div>;
  }

  return (
    <div
      className={classNames({
        [classes.rootWithFooter]: isFooterVisible,
        [classes.desktopRoot]: !isMobile,
      })}
    >
      <Router>
        <Switch>
          {/* Redirect From old message to new messages page - still need because of old emails */}
          {householdResource.withRelationship(fr.relationships.messagesPage.name,
            () => (
              <Redirect
                from={clientPaths.messages}
                to={buildMemberMessagesPageLink(householdResource)}
              />
            ),
            () => null)}
          {householdResource.withRelationship(fr.relationships.dashboard.name,
            () => (
              <Redirect
                from={clientPaths.dashboard}
                to={buildPageLink(householdResource.relationship(fr.relationships.dashboard.name).link('related'), clientPage)}
              />
            ),
            () => null)}
          {householdResource.withRelationship(fr.relationships.messagesPage.name,
            () => (
              <Redirect
                from={clientPaths.activityFeed}
                to={buildMemberMessagesPageLink(householdResource)}
              />
            ),
            () => null)}
          {householdResource.withRelationship(fr.relationships.messagesPage.name,
            () => (
              <Redirect
                from={deprecatedPWAPaths.messages}
                to={buildMemberMessagesPageLink(householdResource)}
              />
            ),
            () => null)}
          {householdResource.withRelationship(fr.relationships.messagesPage.name,
            () => (
              <Redirect
                from={deprecatedPWAPaths.feed}
                to={buildMemberMessagesPageLink(householdResource)}
              />
            ),
            () => null)}

          {/* Mobile redirects */}
          <Redirect
            from={deprecatedPWAPaths.dashboard}
            to={{ pathname: clientPaths.dashboard, search: location.search }}
          />
          <Redirect
            from={deprecatedPWAPaths.tasks}
            to={{ pathname: clientPaths.dashboard, search: location.search }}
          />
          <Redirect
            from={deprecatedPWAPaths.feed}
            to={{ pathname: buildMemberMessagesPageLink(householdResource), search: location.search }}
          />
          <Redirect
            from={deprecatedPWAPaths.files}
            to={{ pathname: clientPaths.files, search: location.search }}
          />
          {householdResource.withRelationship(fr.relationships.householdBalanceSheetPage.name,
            () => (
              <Redirect
                from={deprecatedPWAPaths.accounts}
                to={balanceSheetPageURL(householdResource, householdId, false)}
              />
            ),
            () => null)}

          {/* Routes */}
          <Route
            exact
            path={clientPaths.dashboard}
            render={renderDashboard}
          />
          <Route
            exact
            path={clientGoalsPath}
            render={renderGoals}
          />
          <Route
            exact
            path={clientPaths.files}
            render={renderFiles}
          />
          <Route
            exact
            path={clientPaths.myMeetings}
            render={renderMyMeetings}
          />
          <Route
            path={clientPaths.questionnaire}
            render={renderQuestionnaire}
          />
          <Route
            path={clientMeeting}
            render={renderClientMeeting}
          />
          {householdResource && (
            <Route
              exact
              path={clientPaths.blueprint}
              render={renderBlueprint}
            />
          )}
          {householdResource.withRelationship(fr.relationships.householdBalanceSheetPage.name,
            () => (
              <Redirect
                from={deprecatedClientAccountManagement}
                to={balanceSheetPageURL(householdResource, householdId, false)}
              />
            ),
            () => null)}
          <Route exact path={clientPaths.flow} component={renderFlow} />
        </Switch>
      </Router>
      {!isMobile && <Footer householdResource={householdResource} />}
    </div>
  );
};

export default function ViewportWithAnnouncements() {
  return (
    <ModalProvider>
      <Viewport />
      <AnnouncementDialog />
    </ModalProvider>
  );
}
